import { useEffect, useState, useCallback, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API_BASE } from '@/lib/api';
import { BrandLogo } from '@/components/common/BrandLogo';
import { CheckCircle2, XCircle, AlertTriangle, RefreshCw, Activity, Clock, Database, Wallet, Sparkles, Users } from 'lucide-react';

const ICONS = {
    database: Database,
    users_collection: Users,
    okwallet: Wallet,
    services: Sparkles,
};

export default function StatusPage() {
    const [state, setState] = useState({ loading: true, health: null, error: null, latencyMs: null });
    const [history, setHistory] = useState([]); // last 20 checks
    const [autoRefresh, setAutoRefresh] = useState(true);
    const intervalRef = useRef(null);

    const check = useCallback(async () => {
        const started = Date.now();
        try {
            // Bypass the retry interceptor by using raw axios to see real state
            const { data } = await axios.get(`${API_BASE}/health`, { timeout: 8000 });
            const latency = Date.now() - started;
            setState({ loading: false, health: data, error: null, latencyMs: latency });
            setHistory(h => [{ ts: new Date(), ok: data.ok, latency }, ...h].slice(0, 20));
        } catch (err) {
            const latency = Date.now() - started;
            const isNet = !err.response;
            setState({
                loading: false,
                health: null,
                latencyMs: latency,
                error: isNet
                    ? 'No hay respuesta del backend (posible reinicio en curso)'
                    : `HTTP ${err.response.status}`,
            });
            setHistory(h => [{ ts: new Date(), ok: false, latency }, ...h].slice(0, 20));
        }
    }, []);

    useEffect(() => { check(); }, [check]);

    useEffect(() => {
        if (autoRefresh) {
            intervalRef.current = setInterval(check, 8000);
        }
        return () => clearInterval(intervalRef.current);
    }, [autoRefresh, check]);

    const overall = state.health?.ok === true;
    const restarting = !!state.error && /reinicio|no hay respuesta/i.test(state.error);

    return (
        <div className="min-h-screen bg-gradient-to-b from-[hsl(var(--secondary))]/40 to-transparent" data-testid="status-page">
            <div className="max-w-3xl mx-auto px-4 py-10 space-y-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <BrandLogo size={48} />
                        <div>
                            <h1 className="text-2xl font-semibold">Estado del sistema</h1>
                            <p className="text-xs text-muted-foreground">Ok te lo vendo · monitor público</p>
                        </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={check} disabled={state.loading} data-testid="status-refresh">
                        <RefreshCw className={`h-4 w-4 mr-1 ${state.loading ? 'animate-spin' : ''}`} />Actualizar
                    </Button>
                </div>

                {/* Big status card */}
                <Card className="overflow-hidden">
                    <div className={`h-1.5 ${overall ? 'bg-emerald-500' : restarting ? 'bg-amber-500' : 'bg-rose-500'}`} />
                    <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                            {overall ? (
                                <div className="h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center">
                                    <CheckCircle2 className="h-8 w-8 text-emerald-600" />
                                </div>
                            ) : restarting ? (
                                <div className="h-14 w-14 rounded-full bg-amber-100 flex items-center justify-center">
                                    <AlertTriangle className="h-8 w-8 text-amber-600 animate-pulse" />
                                </div>
                            ) : (
                                <div className="h-14 w-14 rounded-full bg-rose-100 flex items-center justify-center">
                                    <XCircle className="h-8 w-8 text-rose-600" />
                                </div>
                            )}
                            <div className="flex-1">
                                <div className="text-lg font-semibold" data-testid="status-headline">
                                    {overall
                                        ? 'Todos los servicios operativos'
                                        : restarting
                                            ? 'El backend se está reiniciando…'
                                            : 'Servicio con problemas'}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                    {overall
                                        ? `Latencia total: ${state.latencyMs} ms · backend: ${state.health?.elapsed_ms} ms`
                                        : restarting
                                            ? 'Espera unos segundos, la aplicación reintentará automáticamente.'
                                            : state.error || 'Servicio no disponible'}
                                </div>
                            </div>
                            <Badge className={overall ? 'bg-emerald-100 text-emerald-800' : restarting ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800'}>
                                {overall ? 'HEALTHY' : restarting ? 'STARTING' : 'DOWN'}
                            </Badge>
                        </div>
                    </CardContent>
                </Card>

                {/* Component checks */}
                <div className="space-y-2">
                    <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Componentes</h2>
                    {(state.health?.checks || [
                        { name: 'database', ok: false, detail: 'Sin datos' },
                        { name: 'users_collection', ok: false, detail: 'Sin datos' },
                        { name: 'okwallet', ok: false, detail: 'Sin datos' },
                        { name: 'services', ok: false, detail: 'Sin datos' },
                    ]).map(c => {
                        const Icon = ICONS[c.name] || Activity;
                        return (
                            <div key={c.name} className="flex items-center justify-between p-3 rounded-lg border bg-card" data-testid={`status-check-${c.name}`}>
                                <div className="flex items-center gap-3">
                                    <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${c.ok ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                                        <Icon className="h-4 w-4" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-medium capitalize">{c.name.replace('_', ' ')}</div>
                                        <div className="text-xs text-muted-foreground">{c.detail}</div>
                                    </div>
                                </div>
                                {c.ok
                                    ? <Badge className="bg-emerald-100 text-emerald-800">OK</Badge>
                                    : <Badge className="bg-rose-100 text-rose-800">FAIL</Badge>}
                            </div>
                        );
                    })}
                </div>

                {/* History mini graph */}
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-semibold flex items-center gap-1"><Clock className="h-4 w-4" />Últimas comprobaciones</h3>
                            <label className="text-xs flex items-center gap-1 cursor-pointer">
                                <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
                                Auto-refresh 8s
                            </label>
                        </div>
                        <div className="flex items-end gap-1 h-14">
                            {history.length === 0 && <div className="text-xs text-muted-foreground">Sin datos aún.</div>}
                            {history.slice().reverse().map((h, i) => {
                                const maxLat = Math.max(50, ...history.map(x => x.latency || 50));
                                const h_pct = Math.max(6, ((h.latency || 20) / maxLat) * 100);
                                return (
                                    <div
                                        key={i}
                                        title={`${h.ts.toLocaleTimeString('es-MX')} · ${h.latency}ms · ${h.ok ? 'OK' : 'FAIL'}`}
                                        className={`flex-1 rounded-sm ${h.ok ? 'bg-emerald-500' : 'bg-rose-500'}`}
                                        style={{ height: `${h_pct}%`, minHeight: 6 }}
                                    />
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>

                <div className="text-xs text-muted-foreground text-center">
                    Versión API: <strong>{state.health?.version || '—'}</strong>
                    {state.health?.timestamp && <> · Última muestra: {new Date(state.health.timestamp).toLocaleString('es-MX')}</>}
                </div>
            </div>
        </div>
    );
}
